import type { PendientesConfig } from '@/api/installation.api';
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Toggle } from '@/components/ui/Toggle';

/**
 * Pestaña PENDIENTES DEL VENDEDOR.
 *
 * El módulo estaba entero —detección, avisos, escalación por llamada— y no se
 * podía encender: la config existía en el server pero el PUT la recortaba y no
 * había pantalla. Esta es esa pantalla.
 *
 * El interruptor de arriba es el que importa: apagado, el módulo DETECTA y
 * guarda pero no le escribe a nadie. Es el modo en que conviene dejarlo la
 * primera semana —se mira qué detectó antes de que empiece a mandar mensajes a
 * los vendedores—, y por eso se explica en la pantalla en vez de esconderlo
 * detrás de la palabra "sombra".
 */

/**
 * Modelos para la detección, con lo que cuesta cada uno.
 *
 * Se nombra el costo relativo y no el precio: el precio cambia y quedaría
 * mintiendo en la pantalla. Lo que el dueño necesita decidir es si le conviene
 * pagar más por una detección mejor.
 */
const MODELOS = [
  { value: 'claude-opus-5', label: 'El mejor — detecta más, cuesta más' },
  { value: 'claude-sonnet-5', label: 'Intermedio' },
  { value: 'claude-haiku-4-5-20251001', label: 'El más barato — puede dejar pasar alguno' },
];

export function PendientesTab({
  value,
  onChange,
}: {
  value: PendientesConfig;
  onChange: (patch: Partial<PendientesConfig>) => void;
}) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Compromisos con clientes</CardTitle>
          <CardDescription>
            Lee los chats y detecta lo que el equipo le prometió a un cliente —llamar, mandar una
            cotización, pasar un precio— para que no se pierda.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex items-start gap-3 rounded-md border border-slate-200 p-3">
            <Toggle
              checked={value.activo}
              onChange={(activo) => onChange({ activo })}
              label="Avisarle al vendedor"
            />
            <div className="text-sm">
              <p className="font-medium text-slate-700">
                {value.activo ? 'Avisa por WhatsApp' : 'Solo mira, no avisa'}
              </p>
              <p className="text-slate-500">
                {value.activo
                  ? 'Cuando detecta un compromiso, le escribe al vendedor a cargo.'
                  : 'Detecta y los muestra en Pendientes, pero no le escribe a nadie. Conviene dejarlo así la primera semana: mire qué detecta antes de que empiece a mandar mensajes.'}
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="No molestar antes de las"
              type="time"
              value={value.ventana.desde}
              onChange={(e) => onChange({ ventana: { ...value.ventana, desde: e.target.value } })}
            />
            <Input
              label="Ni después de las"
              type="time"
              value={value.ventana.hasta}
              onChange={(e) => onChange({ ventana: { ...value.ventana, hasta: e.target.value } })}
            />
          </div>
          <p className="text-xs text-slate-500">
            Fuera de esa franja no sale ningún aviso: queda esperando al día siguiente.
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Cuando el vendedor no contesta</CardTitle>
          <CardDescription>
            Si el aviso queda sin respuesta, el sistema puede llamarlo por teléfono.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex items-start gap-3 rounded-md border border-slate-200 p-3">
            <Toggle
              checked={value.llamadas.activo}
              onChange={(activo) => onChange({ llamadas: { ...value.llamadas, activo } })}
              label="Llamar por teléfono"
            />
            <div className="text-sm">
              <p className="font-medium text-slate-700">
                {value.llamadas.activo ? 'Llama' : 'No llama'}
              </p>
              <p className="text-slate-500">
                Va aparte del interruptor de arriba: puede avisar por WhatsApp sin llamar nunca.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Llamar si no contesta en (minutos)"
              type="number"
              min={1}
              value={String(value.llamadas.minutosEscalacion)}
              onChange={(e) =>
                onChange({
                  llamadas: { ...value.llamadas, minutosEscalacion: Number(e.target.value) },
                })
              }
            />
            <div>
              <Input
                label="Si es una venta, en (minutos)"
                type="number"
                min={1}
                value={String(value.llamadas.minutosEscalacionVenta)}
                onChange={(e) =>
                  onChange({
                    llamadas: {
                      ...value.llamadas,
                      minutosEscalacionVenta: Number(e.target.value),
                    },
                  })
                }
              />
              <p className="mt-1 text-xs text-slate-500">
                Más corto a propósito: una venta que se enfría no se recupera.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Cuánto lee y con qué</CardTitle>
          <CardDescription>
            Estos números deciden cuánto cuesta el módulo por día. Suba con cuidado.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Input
                label="Chats que lee por vuelta"
                type="number"
                min={1}
                max={200}
                value={String(value.maxChatsPorCorrida)}
                onChange={(e) => onChange({ maxChatsPorCorrida: Number(e.target.value) })}
              />
              <p className="mt-1 text-xs text-slate-500">
                Bajo a propósito. Este número es lo que el módulo gasta: si se dispara, puede
                alcanzar el tope de gasto de IA del día y dejar a la asesora sin contestarle a
                los clientes.
              </p>
            </div>
            <div>
              <Input
                label="Solo chats con actividad en los últimos (días)"
                type="number"
                min={1}
                value={String(value.soloChatsActivosDias)}
                onChange={(e) => onChange({ soloChatsActivosDias: Number(e.target.value) })}
              />
              <p className="mt-1 text-xs text-slate-500">
                Sin este corte, la primera vuelta leería el historial entero y el panel se
                llenaría de compromisos viejos que además nacen vencidos.
              </p>
            </div>
          </div>

          <Select
            label="Con qué modelo lee"
            value={value.modelo}
            options={MODELOS}
            onChange={(e) => onChange({ modelo: e.target.value })}
          />
          <p className="text-xs text-slate-500">
            Es independiente del modelo de la asesora: cambiar uno no cambia el otro.
          </p>

          <Input
            label="Horas de gracia antes de darlo por vencido"
            type="number"
            min={0}
            value={String(value.horasMargenVencimiento)}
            onChange={(e) => onChange({ horasMargenVencimiento: Number(e.target.value) })}
          />
        </CardBody>
      </Card>
    </div>
  );
}
